import type {FeatureServiceBinder, Logger} from '@feature-hub/core';
import type {LocaleV1} from './create-binder-v1.js';
import type {LocaleListenerV3, LocaleV3} from './create-binder-v3.js';
import {getCountryCode} from './get-country-code.js';
import {getCurrency} from './get-currency.js';
import {getLanguage} from './get-language.js';

export interface LocaleServiceV2 {
  readonly getLocale: () => LocaleV2;
  readonly subscribe: (listener: LocaleListenerV2) => () => void;
}

export interface LocaleV2 extends LocaleV1 {
  /**
   * IETF BCP 47 language tag
   */
  readonly languageTag: string;
  /**
   * Base site identifier, used for requests to MuleSoft
   */
  readonly baseSiteId: string;
  /**
   * The Market ID identifies a market, and is primarily used for fetching
   * content from AEM, e.g.
   * `/content/dam/smart/feature-apps/content/example/${marketId}/${language}/${contentFragmentName}`
   */
  readonly marketId: string;
}

export type LocaleListenerV2 = (locale: LocaleV2) => void;

export interface CreateBinderV2Options {
  readonly locale: LocaleV3;
  readonly logger: Logger;
  readonly subscribe: (listener: LocaleListenerV3) => void;
}

export function createBinderV2(
  options: CreateBinderV2Options,
): FeatureServiceBinder<LocaleServiceV2> {
  const listeners = new Set<LocaleListenerV2>();
  let locale = createLocaleV2(options.locale);

  options.subscribe((newLocale) => {
    locale = createLocaleV2(newLocale);

    for (const listener of listeners) {
      listener(locale);
    }
  });

  return (consumerId, consumerName) => {
    options.logger.warn(
      `DEPRECATED: Version 2.x.x of the locale service is deprecated. The consumer "${consumerId}"${
        consumerName ? ` ("${consumerName}")` : ``
      } should use 3.0.0 instead.`,
    );

    return {
      featureService: {
        getLocale: () => locale,

        subscribe: (listener) => {
          listeners.add(listener);

          return () => {
            listeners.delete(listener);
          };
        },
      },
    };
  };
}

function createLocaleV2(locale: LocaleV3): LocaleV2 {
  const {marketId, languageTag} = locale;
  const countryCode = getCountryCode(marketId);

  return {
    countryCode,
    language: getLanguage(languageTag),
    languageTag,
    currency: getCurrency(marketId),
    baseSiteId: `smart-${countryCode.toLocaleLowerCase()}`,
    marketId,
  };
}
